import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import i18n from '../i18n';

export interface AppError {
  id: string;
  code: string;
  message: string;
  status?: number;
  details?: any;
  timestamp: string;
  requestId?: string;
  type: 'error' | 'warning' | 'info';
  persistent?: boolean;
}

interface ErrorContextType {
  errors: AppError[];
  showError: (error: Partial<AppError>) => void;
  showWarning: (message: string, details?: any) => void;
  showInfo: (message: string, details?: any) => void;
  dismissError: (id: string) => void;
  clearAllErrors: () => void;
  getErrorByCode: (code: string) => AppError | undefined;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<AppError[]>([]);

  const generateId = useCallback(() => {
    return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showError = useCallback((error: Partial<AppError>) => {
    const fullError: AppError = {
      id: error.id || generateId(),
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || i18n.t('contexts.errorContext.defaultErrorMessage'),
      status: error.status,
      details: error.details,
      timestamp: error.timestamp || new Date().toISOString(),
      requestId: error.requestId,
      type: error.type || 'error',
      persistent: error.persistent || false
    };

    setErrors(prev => {
      // Don't add duplicate errors with same code and message
      const exists = prev.some(e => 
        e.code === fullError.code && e.message === fullError.message
      );
      
      if (exists) {
        return prev;
      }
      
      return [...prev, fullError];
    });

    // Auto-dismiss non-persistent errors after 8 seconds
    if (!fullError.persistent) {
      setTimeout(() => {
        dismissError(fullError.id);
      }, 8000);
    }
  }, [generateId]);

  const showWarning = useCallback((message: string, details?: any) => {
    showError({
      code: 'WARNING',
      message,
      details,
      type: 'warning'
    });
  }, [showError]);

  const showInfo = useCallback((message: string, details?: any) => {
    showError({
      code: 'INFO',
      message,
      details,
      type: 'info'
    });
  }, [showError]);

  const dismissError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const getErrorByCode = useCallback((code: string) => {
    return errors.find(error => error.code === code);
  }, [errors]);

  const value: ErrorContextType = {
    errors,
    showError,
    showWarning,
    showInfo,
    dismissError,
    clearAllErrors,
    getErrorByCode
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error(i18n.t('contexts.errorContext.useErrorOutsideProvider'));
  }
  return context;
};

// Helper hooks for common error scenarios
export const useApiError = () => {
  const { showError } = useError();
  
  const handleApiError = useCallback((error: any) => {
    showError({
      code: error.code || `HTTP_${error.status || 500}`,
      message: error.message || i18n.t('contexts.errorContext.apiErrorOccurred'),
      status: error.status,
      details: error.details || error.response?.data,
      requestId: error.requestId,
      type: 'error'
    });
  }, [showError]);

  return { handleApiError };
};

export const useAuthError = () => {
  const { showError } = useError();
  
  const handleAuthError = useCallback((error: any) => {
    const isAuthError = error.status === 401 || error.code === 'AUTHENTICATION_REQUIRED';
    
    showError({
      code: error.code || 'AUTH_ERROR',
      message: error.message || i18n.t('contexts.errorContext.authenticationFailed'),
      status: error.status,
      details: error.details,
      type: 'error',
      persistent: isAuthError // Keep auth errors visible until user action
    });
  }, [showError]);

  return { handleAuthError };
};